/**
 * Sprint v1.7 Phase 4 (Do 03.09.2026) — Cockpit-Sidebar mit 3 Phasen + VideoKurs.
 *
 * Permanente Sidebar links auf allen Cockpit-Seiten.
 * Vier Punkte (Selfmanagement-Toolbox EXPLIZIT NICHT — Addon in Trainings/Kurse):
 *   1. Mein Muster       — Selbsterkenntnis (Phase 1, Anker #phase-mein-muster)
 *   2. Andere verstehen  — Fokustyp-Radar + Zugangscode (Phase 2, Anker #phase-andere-verstehen)
 *   3. Wirkung gestalten — Kommunikation gestalten (Phase 3, Anker #phase-wirkung-gestalten)
 *   4. VideoKurs         — /dashboard/videos (externer Link)
 *
 * Sprint-Auftrag Do 03.09.2026 12:43 · Option Gamma · Pre-Launch-Scope.
 * MarketingSecrets.ai-Pattern für User-Orientierung + Churn-Reduktion.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User, Eye, DoorOpen, PlayCircle } from "lucide-react";

const PHASEN = [
  {
    step: 1,
    id: "phase-mein-muster",
    label: "Mein Muster",
    sub: "Selbsterkenntnis",
    icon: User,
    kind: "anchor", // scroll-anchor auf /cockpit
  },
  {
    step: 2,
    id: "phase-andere-verstehen",
    label: "Andere verstehen",
    sub: "Andere-Kenntnis",
    icon: Eye,
    kind: "anchor",
  },
  {
    step: 3,
    id: "phase-wirkung-gestalten",
    label: "Wirkung gestalten",
    sub: "Gestaltung",
    icon: DoorOpen,
    kind: "anchor",
  },
  {
    step: null,
    id: "videokurs",
    label: "VideoKurs",
    sub: "Grundlagen & mehr",
    icon: PlayCircle,
    kind: "route",
    to: "/dashboard/videos",
  },
];

export default function CockpitSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("phase-mein-muster");

  const isOnCockpit = location.pathname === "/cockpit";

  // Scroll-Spy: aktive Phase basierend auf Viewport-Position
  useEffect(() => {
    if (!isOnCockpit) return;
    const anchors = PHASEN.filter((p) => p.kind === "anchor").map((p) => p.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 },
    );
    anchors.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isOnCockpit]);

  const handleAnchorClick = (id) => (e) => {
    e.preventDefault();
    if (isOnCockpit) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setActiveId(id);
      }
    } else {
      // Von Sub-Seite: erst zu /cockpit navigieren, dann via hash scrollen
      navigate(`/cockpit#${id}`);
    }
  };

  return (
    <aside
      className="hidden lg:block sticky top-0 h-screen w-64 bg-white border-r border-slate-200 py-8 px-4 flex-shrink-0"
      data-testid="cockpit-sidebar"
      aria-label="Navigation"
    >
      <div className="mb-6 px-2">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          Deine 3 Phasen
        </p>
        <p className="mt-1 text-xs text-slate-500 leading-snug">
          Vom eigenen Muster über den anderen bis zur Wirkung.
        </p>
      </div>

      <nav className="space-y-1" aria-label="Phasen-Navigation">
        {PHASEN.map((phase) => {
          const Icon = phase.icon;
          const isActive =
            phase.kind === "anchor"
              ? isOnCockpit && activeId === phase.id
              : location.pathname.startsWith(phase.to || "");

          const commonClasses = [
            "flex items-start gap-3 w-full text-left rounded-xl px-3 py-2.5 transition-all",
            isActive
              ? "bg-[var(--fts-primary-bright)] text-white shadow-sm"
              : "text-slate-700 hover:bg-slate-50",
          ].join(" ");

          const iconWrapClasses = [
            "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
            isActive
              ? "bg-white/15"
              : "bg-[var(--fts-primary-bright)]/10 text-[var(--fts-primary-bright)]",
          ].join(" ");

          const content = (
            <>
              <div className={iconWrapClasses} aria-hidden="true">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  {phase.step !== null && (
                    <span
                      className={[
                        "text-[10px] font-bold tabular-nums",
                        isActive ? "text-white/70" : "text-slate-400",
                      ].join(" ")}
                    >
                      {phase.step}
                    </span>
                  )}
                  <span className="text-sm font-bold leading-tight">
                    {phase.label}
                  </span>
                </div>
                <p
                  className={[
                    "text-xs leading-snug mt-0.5",
                    isActive ? "text-white/85" : "text-slate-500",
                  ].join(" ")}
                >
                  {phase.sub}
                </p>
              </div>
            </>
          );

          if (phase.kind === "route") {
            return (
              <Link
                key={phase.id}
                to={phase.to}
                data-testid={`sidebar-link-${phase.id}`}
                data-active={isActive ? "true" : "false"}
                className={commonClasses}
              >
                {content}
              </Link>
            );
          }
          return (
            <a
              key={phase.id}
              href={`#${phase.id}`}
              onClick={handleAnchorClick(phase.id)}
              data-testid={`sidebar-link-${phase.id}`}
              data-active={isActive ? "true" : "false"}
              className={commonClasses}
            >
              {content}
            </a>
          );
        })}
      </nav>

      <div className="mt-8 mx-2 pt-4 border-t border-slate-100">
        <p className="text-xs text-slate-400 leading-snug">
          Selfmanagement-Toolbox findest du in deinem Videokurs-Bereich.
        </p>
      </div>
    </aside>
  );
}
